import router from '@/router'
import RasaSessionsService from '@/services/sessions'
import { mountSessionById, mountMessagesBySender } from "@/assets/js/rasaBuildConversations"

const state = {
    closedSessions: [], // Lote atual exibido
    allClosedSessions: [], // Todas as sessões fechadas em memória
    closedSessionsBatchSize: 25,
    closedSessionsCurrentBatch: 1,
    isLoadingClosedSessions: false,
    closedSessionsAbortController: null,
    closedSessionsLoadRequestId: 0,
}

const mutations = {
    setAllClosedSessions(state, sessions) {
        state.allClosedSessions = sessions
    },
    resetAllClosedSessions(state) {
        state.allClosedSessions = []
        state.closedSessions = []
        state.closedSessionsCurrentBatch = 1
    },
    setClosedSessionsLoading(state, value) {
        state.isLoadingClosedSessions = value
    },
    setClosedSessionsAbortController(state, controller) {
        state.closedSessionsAbortController = controller
    },
    setClosedSessionsLoadRequestId(state, requestId) {
        state.closedSessionsLoadRequestId = requestId
    },
    setClosedSessions(state, sessions) {
        state.closedSessions = sessions
    },
    nextClosedSessionsBatch(state) {
        state.closedSessionsCurrentBatch++
        const end = state.closedSessionsCurrentBatch * state.closedSessionsBatchSize
        state.closedSessions = state.allClosedSessions.slice(0, end)
    },
    resetClosedSessions(state) {
        state.closedSessions = []
        state.closedSessionsCurrentBatch = 1
    },
    orderAllClosedSessions(state) {
        state.allClosedSessions = state.allClosedSessions.sort((session1, session2) => {
          if(session1.session.updated_at > session2.session.updated_at) return -1
          return 1
        })
    },
    deleteSession(state, idx) {
        state.closedSessions.splice(idx, 1)
    },
}

const actions = {
    async getClosedSessions({ rootState, commit, state, dispatch }) {
        if (state.isLoadingClosedSessions) {
            return
        }

        if (state.allClosedSessions.length === 0) {
            const requestId = state.closedSessionsLoadRequestId + 1
            const abortController = new AbortController()

            commit('setClosedSessionsLoadRequestId', requestId)
            commit('setClosedSessionsAbortController', abortController)
            commit('setClosedSessionsLoading', true)

            let params = {
                user_id: rootState.auth.loggedUser.id,
                fields_only: ['id', 'sender'],
                status: 'CLOSING,WAITING_CUSTOMER',
                order: '-updated_at',
                service_id: rootState.rasaServices.currentService.id,
            }

            try {
                let response = await RasaSessionsService.get_sessions_by_params(params, { signal: abortController.signal }).then(res => res.data)
                let allSessions = []

                for (const currentSession of response.sessions) {
                    if (state.closedSessionsLoadRequestId !== requestId) {
                        return
                    }

                    let session = await mountSessionById(
                        currentSession.id,
                        rootState.rasaServices.currentService.id,
                        currentSession.sender,
                        { signal: abortController.signal }
                    )

                    if (!session) {
                        continue
                    }

                    session.messages = await mountMessagesBySender({
                        sender: session.session.sender,
                        service_id: session.session.service_id,
                        old_get_messages: session.messages,
                        requestConfig: { signal: abortController.signal }
                    })

                    allSessions.push(session)
                }

                if (state.closedSessionsLoadRequestId !== requestId) {
                    return
                }

                commit('setAllClosedSessions', allSessions)
                commit('orderAllClosedSessions')
                // Inicializa o primeiro lote
                commit('setClosedSessions', allSessions.slice(0, state.closedSessionsBatchSize))

                if (state.closedSessions.length > 0) {
                    dispatch('rasaConversation/changeCurrentConversation', state.closedSessions[0], { root: true })
                } else {
                    dispatch('rasaConversation/clearCurrentConversation', {},{ root: true })
                }
            } catch (error) {
                if (error && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')) {
                    return
                }

                console.error('Erro ao carregar sessões fechadas', error)
            } finally {
                if (state.closedSessionsLoadRequestId === requestId) {
                    commit('setClosedSessionsLoading', false)
                    commit('setClosedSessionsAbortController', null)
                }
            }
        }
    },
    cancelClosedSessionsLoading({ state, commit }) {
        commit('setClosedSessionsLoadRequestId', state.closedSessionsLoadRequestId + 1)

        if (state.closedSessionsAbortController) {
            state.closedSessionsAbortController.abort()
        }

        commit('setClosedSessionsAbortController', null)
        commit('setClosedSessionsLoading', false)
    },
    loadNextClosedSessionsBatch({ state, commit }) {
        if (state.closedSessions.length < state.allClosedSessions.length) {
            commit('nextClosedSessionsBatch')
        }
    },
    clearClosedSessionsCache({ dispatch, commit }) {
        dispatch('cancelClosedSessionsLoading')
        commit('resetAllClosedSessions')
    },
    async removeClosedConversation({ rootState, commit, state, dispatch }, idx) {
        commit('deleteSession', idx)
        dispatch('rasaConversation/changeCurrentConversation', state.closedSessions[idx], { root: true })
    }
}

const getters = {
    closedSessions: (state) => state.closedSessions,
    allClosedSessions: (state) => state.allClosedSessions,
    closedSessionsHasMore: (state) => state.closedSessions.length < state.allClosedSessions.length,
    isLoadingClosedSessions: (state) => state.isLoadingClosedSessions,
}

  export default {
      state,
      mutations,
      actions,
      getters,
      namespaced: true,
  };